import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Waypoints } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.removeItem("impersonate_token");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      await refresh();
      navigate(data.user.role === "super_admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between bg-neutral-900 text-white p-16">
        <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
          <Waypoints className="h-5 w-5" />
          <span className="font-heading font-bold">CreateForms.app</span>
        </Link>
        <div>
          <h2 className="text-4xl font-black leading-tight">Welcome back to your customer hub.</h2>
          <p className="mt-4 text-neutral-400 max-w-md">Forms, uploads and conversations — all waiting exactly where you left them.</p>
        </div>
        <p className="text-xs text-neutral-500">CreateForms.app — Customer Journey Management</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div>
            <h1 className="text-3xl font-black">Log in</h1>
            <p className="text-sm text-neutral-500 mt-2">Business & admin accounts.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-neutral-500 hover:text-neutral-900 underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-12" data-testid="login-submit-button">
            {loading ? "Logging in…" : "Log in"}
          </Button>
          <p className="text-sm text-neutral-500">
            No account? <Link to="/register" className="text-neutral-900 font-medium underline" data-testid="login-register-link">Create one</Link>
          </p>
          <p className="text-sm text-neutral-500">
            Customer? <Link to="/customer/login" className="text-neutral-900 font-medium underline" data-testid="login-customer-link">Get a login link</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
